import { LoaderCircle } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useApi } from 'shared_components/context';
import { ArtistImageNotFound } from './ArtistImageNotFound';
import { useRateLimiter } from './RateLimiterProvider';

export function ArtistDiscogImage({
  discogId,
  title,
}: {
  discogId: string;
  title: string;
}) {
  const [loadingImage, setLoadingImage] = useState(false);
  const api = useApi();
  const { wrapApiCall } = useRateLimiter();
  const [imageSrc, setImageSrc] = useState<string | null>(() => {
    try {
      const saved_image = localStorage.getItem(
        `discogs_artist_image_${discogId}`
      );
      if (typeof saved_image === 'string' && saved_image.length > 0)
        return saved_image;
      return null;
    } catch {
      return null;
    }
  });
  const placeholderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Do not fetch if we already have an image
    if (typeof imageSrc === 'string' && imageSrc.length > 0) return;

    const element = placeholderRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      // Fetch when the element is visible
      if (entry.isIntersecting) {
        // We can unobserve the element now
        observer.unobserve(element);

        setLoadingImage(true);

        wrapApiCall(() =>
          api.adminApi.searchArtistDiscogsArtist({
            discogsId: discogId,
          })
        )
          .then((data) => {
            const artist_image = data.images?.[0]?.resourceUrl ?? 'none';
            localStorage.setItem(
              `discogs_artist_image_${discogId}`,
              artist_image
            );
            setImageSrc(artist_image);
          })
          .catch((err) => {
            if (
              err.response?.status === 404 ||
              err.response?.status === 500 ||
              err.response?.status === 400
            ) {
              localStorage.setItem(`discogs_artist_image_${discogId}`, 'none');
              setImageSrc('none');
              return;
            }
            console.error(
              `Failed to fetch image for discogId ${discogId}:`,
              err
            );
            localStorage.removeItem(`discogs_artist_image_${discogId}`);
            setImageSrc(null);
          })
          .finally(() => {
            setLoadingImage(false);
          });
      }
    });

    observer.observe(element);

    return () => {
      // Cleanup observer on component unmount
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [discogId, api, imageSrc, wrapApiCall]);

  if (loadingImage) {
    return (
      <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
        <LoaderCircle className="tw-animate-spin" />
      </div>
    );
  }

  if (imageSrc === 'none') return <ArtistImageNotFound />;

  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={title}
        className="tw-w-full tw-h-full tw-object-cover"
      />
    );
  }

  // Render a placeholder to be observed
  return (
    <div
      ref={placeholderRef}
      className="tw-w-full tw-h-full"
      role="img"
      aria-label={title}
    />
  );
}
